<script lang="ts">
	import PrivateAppShell from '$lib/layouts/private-app-shell.svelte';
	import { TOOLTIP_PORTAL_DISABLED_CONTEXT } from '$lib/components/ui/tooltip/portal-context.js';
	import { setHessianClientContext } from '$lib/context/hessianClientContext.svelte.js';
	import { setOrganizationContext } from '$lib/context/organizationContext.svelte.js';
	import type { User } from '@supabase/supabase-js';
	import { onDestroy, onMount, setContext } from 'svelte';
	import Dashboard from '../../(private)/dashboard/+page.svelte';
	import {
		createMockHessianClient,
		MOCK_ORGANIZATION_ID,
		mockOrganization,
		mockUser,
		SPLASH_METRIC_CARDS
	} from './splash-dashboard-mocks';

	let shellEl = $state<HTMLDivElement | null>(null);

	// Seed both contexts with mocks before Dashboard's script runs. The mock
	// client returns preset data synchronously — no network calls, no auth.
	const mockClient = createMockHessianClient();
	setHessianClientContext(() => mockClient);
	setOrganizationContext(() => ({ id: MOCK_ORGANIZATION_ID, slug: 'acme', personal: false }), {
		persist: false
	});

	// Tell descendant tooltips to skip the body portal. The whole showcase sits
	// inside a 3D-rotated container; portalled tooltips otherwise render at
	// flat screen coordinates and visibly detach from the rotated plane.
	setContext(TOOLTIP_PORTAL_DISABLED_CONTEXT, true);

	// Dashboard expects `data.user` + `data.queryClient` (only touched on URL
	// param 'joined' — never true here). A stub is enough.
	const dashboardData = {
		user: null,
		queryClient: {
			invalidateQueries: () => Promise.resolve()
		},
		title: '',
		description: ''
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} as any;

	// Swallow activation-style events at the capture phase so clicks,
	// pointer downs (which is how dropdowns/dialogs actually open), and
	// form submits inside the preview don't trigger real navigation or
	// open portalled popovers whose contents escape our DOM subtree.
	// Hover / focus / pointermove are untouched so CSS :hover states and
	// tooltip open-on-hover keep working.
	function blockActivation(e: Event) {
		e.preventDefault();
		e.stopPropagation();
	}

	function blockNavKeys(e: KeyboardEvent) {
		// Enter / Space on a focused link or button would otherwise navigate.
		if (e.key === 'Enter' || e.key === ' ') {
			e.preventDefault();
			e.stopPropagation();
		}
	}

	// Pull every focusable descendant out of the Tab order. The 3D-rotated
	// 1920px container gives focusables a post-transform viewport rect that
	// sits far from where they visually appear; letting Tab land inside
	// triggers the browser's scrollIntoView() and yanks the whole showcase
	// off-screen. Setting tabindex="-1" on each focusable (rather than using
	// `inert`) keeps pointer events alive so hover-driven tooltips and
	// dropdowns continue to fire normally.
	const FOCUSABLE = [
		'a[href]',
		'button',
		'input',
		'select',
		'textarea',
		'iframe',
		'audio[controls]',
		'video[controls]',
		'details',
		'summary',
		'[contenteditable]:not([contenteditable="false"])',
		'[tabindex]:not([tabindex="-1"])'
	].join(',');

	function disableTabbingIn(root: Element) {
		root.querySelectorAll<HTMLElement>(FOCUSABLE).forEach((el) => {
			if (el.getAttribute('tabindex') !== '-1') el.setAttribute('tabindex', '-1');
		});
	}

	// `@hessianhq/ui`'s `ChartTooltip` (the Workflow Runs scatter on the
	// dashboard) sets its position with `left/top` derived from the pointer's
	// `clientX/clientY` — i.e. viewport coordinates. The element uses
	// `position: fixed`, which CSS demotes to "absolute relative to the
	// nearest transformed ancestor" whenever such an ancestor exists. Here
	// that ancestor is the splash's rotated 1920px plate, so the viewport
	// values land in the plate's *local* coord system and the tip drifts off
	// the cursor (the user's "y-offset" complaint).
	//
	// Lifting the tip to <body> escapes the transform but also loses the
	// rotation that visually integrates it into the plate. Instead we keep
	// the tip in place (so it inherits the plate's rotation for free) and
	// rewrite its `left/top` to the local coords that *project to* the
	// pointer's viewport position. A 1px hidden probe inside the same
	// containing block samples the local→viewport projection on each
	// rewrite, so the math stays correct under scroll / resize / page-flow
	// shifts of the plate.
	let positionProbe: HTMLDivElement | null = null;
	let correctingTip = false;

	function sampleLocalToViewport() {
		if (!positionProbe) return null;
		positionProbe.style.left = '0px';
		positionProbe.style.top = '0px';
		const o = positionProbe.getBoundingClientRect();
		positionProbe.style.left = '100px';
		const x = positionProbe.getBoundingClientRect();
		positionProbe.style.left = '0px';
		positionProbe.style.top = '100px';
		const y = positionProbe.getBoundingClientRect();
		// V = O + Mx · Lx + My · Ly; columns of the 2×2 projection matrix
		// give per-unit-local displacement in viewport space.
		return {
			ox: o.x,
			oy: o.y,
			mxx: (x.x - o.x) / 100,
			mxy: (x.y - o.y) / 100,
			myx: (y.x - o.x) / 100,
			myy: (y.y - o.y) / 100
		};
	}

	function viewportToLocal(vx: number, vy: number) {
		const p = sampleLocalToViewport();
		if (!p) return null;
		const dx = vx - p.ox;
		const dy = vy - p.oy;
		const det = p.mxx * p.myy - p.mxy * p.myx;
		if (det === 0) return null;
		return {
			x: (dx * p.myy - dy * p.myx) / det,
			y: (-dx * p.mxy + dy * p.mxx) / det
		};
	}

	function correctTipPosition(tip: HTMLElement) {
		if (correctingTip) return;
		const left = parseFloat(tip.style.left);
		const top = parseFloat(tip.style.top);
		if (!Number.isFinite(left) || !Number.isFinite(top)) return;
		const local = viewportToLocal(left, top);
		if (!local) return;
		correctingTip = true;
		tip.style.left = `${local.x}px`;
		tip.style.top = `${local.y}px`;
		// Re-arm after the mutation observer fires for our write so the
		// next genuine chart-tooltip update is corrected too.
		queueMicrotask(() => {
			correctingTip = false;
		});
	}

	function correctAllTips(root: Element) {
		if (root instanceof HTMLElement && root.classList.contains('hk-ctt')) {
			correctTipPosition(root);
		}
		root.querySelectorAll?.<HTMLElement>('.hk-ctt').forEach(correctTipPosition);
	}

	let observer: MutationObserver | null = null;

	onMount(() => {
		if (!shellEl) return;
		disableTabbingIn(shellEl);

		// Probe lives inside `.splash-shell`, which is itself a child of
		// the rotated plate — so it shares ChartTooltip's containing
		// block. Hidden via 1px size + transparent + pointer-events:none.
		positionProbe = document.createElement('div');
		positionProbe.setAttribute('aria-hidden', 'true');
		positionProbe.style.cssText =
			'position: fixed; left: 0; top: 0; width: 1px; height: 1px; pointer-events: none; opacity: 0;';
		shellEl.appendChild(positionProbe);

		correctAllTips(shellEl);
		observer = new MutationObserver((mutations) => {
			for (const m of mutations) {
				if (m.type === 'childList') {
					m.addedNodes.forEach((node) => {
						if (node instanceof Element) {
							disableTabbingIn(node);
							correctAllTips(node);
						}
					});
				} else if (m.type === 'attributes' && m.target instanceof HTMLElement) {
					const target = m.target;
					if (m.attributeName === 'style' && target.classList.contains('hk-ctt')) {
						correctTipPosition(target);
					} else if (
						target.matches(FOCUSABLE) &&
						target.getAttribute('tabindex') !== '-1'
					) {
						// Something (bits-ui, a shortcut binding) tried to restore
						// tab-ability — flip it right back.
						target.setAttribute('tabindex', '-1');
					}
				}
			}
		});
		observer.observe(shellEl, {
			childList: true,
			subtree: true,
			attributes: true,
			attributeFilter: ['tabindex', 'href', 'disabled', 'style']
		});
	});

	onDestroy(() => {
		observer?.disconnect();
		positionProbe?.remove();
		positionProbe = null;
	});
</script>

<div
	class="mx-auto inline-flex max-h-screen w-full justify-center select-none perspective-[4000px]"
	aria-hidden="true"
>
	<div
		class="bg-background linear-gradient-bottom-fade-out w-[1920px] rotate-[353deg] rotate-x-[15deg] rotate-y-[15deg] p-1 transform-3d"
	>
		<!-- Mac-style chrome — lives inside the 3D-rotated container so it
			 lines up with the rotated dashboard beneath it. -->
		<div
			class="border-border bg-surface-raised flex items-center gap-2 rounded-t-xl border border-b-0 px-4 py-3"
		>
			<div class="flex items-center gap-2">
				<span
					class="inline-block size-3.5 rounded-full"
					style="background: oklch(0.6 0.18 25); opacity: 0.7;"
				></span>
				<span
					class="inline-block size-3.5 rounded-full"
					style="background: oklch(0.7 0.15 80); opacity: 0.7;"
				></span>
				<span
					class="inline-block size-3.5 rounded-full"
					style="background: oklch(0.7 0.18 145); opacity: 0.7;"
				></span>
			</div>
			<div class="flex-1"></div>
			<span class="text-soft-gray pr-2 font-mono text-sm">acme.hessian.sh</span>
			<div class="flex-1"></div>
			<div class="invisible flex items-center gap-2">
				<span class="inline-block size-3.5 rounded-full"></span>
				<span class="inline-block size-3.5 rounded-full"></span>
				<span class="inline-block size-3.5 rounded-full"></span>
			</div>
		</div>

		<!-- Real app shell (PrivateAppShell) bound to mock contexts. Clicks
			 + submits are swallowed at the capture phase so navigation fires
			 nothing; Tab focus is removed via the onMount sweep above so
			 focus-into-3D-transform doesn't yank the page; hover / focus
			 / tooltip behaviour all stay alive.

			 Passing `env="prod"` suppresses the EnvBanner (the splash isn't
			 a real deployment); `disableShortcuts` skips the global ⌘K
			 binding inside the shell (would otherwise dump a portalled
			 search modal onto the home page); `resizable={false}` removes
			 the sidebar drag handle (no place for it inside the 3D plate). -->
		<div
			bind:this={shellEl}
			class="splash-shell bg-background border-border overflow-hidden rounded-b-xl border border-t-0"
			role="presentation"
			onclickcapture={blockActivation}
			onauxclickcapture={blockActivation}
			onpointerdowncapture={blockActivation}
			onmousedowncapture={blockActivation}
			onsubmitcapture={blockActivation}
			onkeydowncapture={blockNavKeys}
		>
			<PrivateAppShell
				user={mockUser as unknown as User}
				organization={mockOrganization}
				organizationId={MOCK_ORGANIZATION_ID}
				hessianClient={mockClient}
				env="prod"
				disableShortcuts
				resizable={false}
			>
				<Dashboard
					data={dashboardData}
					disableHeaderTransition
					splashMetrics={SPLASH_METRIC_CARDS}
				/>
			</PrivateAppShell>
		</div>
	</div>
</div>

<style>
	/* The splash mounts a second copy of the production app shell inside
	   a 3D-rotated marketing plate. Strip the view-transition names off
	   the shell chrome on the splash instance only — otherwise the
	   browser tries to morph the splash's (rotated, 1920px-wide)
	   sidebar + collapse toggle into the real (un-rotated, viewport-
	   width) sidebar when the visitor navigates from / to /dashboard,
	   which renders as a glitchy swoop instead of the intended
	   cross-fade. The same instance-scoped trick is what
	   `disableHeaderTransition` does for the dashboard's own header. */
	.splash-shell :global(.app-sb),
	.splash-shell :global(.app-sb-toggle-wrap),
	.splash-shell :global(.hessian-app-envbanner) {
		view-transition-name: none;
	}

	/* Page-scroll fix. The production `.app-inset` uses `overflow: auto`
	   so the inset card scrolls in place inside a 100vh `.app-frame`.
	   On the splash, the showcase sits inside the normally-flowing
	   marketing page; that internal scroller intercepts wheel events
	   whenever the cursor is over the dashboard, leaving visitors
	   stuck and unable to scroll past it. Switching to `hidden` makes
	   the inset's y-overflow non-scrollable so wheel events bubble up
	   to the document. The outer `.splash-shell` already clips
	   (`overflow: hidden`), so content past the visible plate is
	   cleanly cut off by the marketing chrome — no scrollbar appears. */
	.splash-shell :global(.app-inset) {
		overflow: hidden;
	}

	/* The shell's mobile media query (@media max-width: 900px) hides the
	   sidebar so /pages/* docs examples can claim the full viewport. The
	   splash is locked at 1920px wide and rendered as a static preview,
	   so it should keep showing the sidebar regardless of the viewport
	   width that's hosting it. */
	@media (max-width: 900px) {
		.splash-shell :global(.app-sb) {
			display: flex !important;
		}
		.splash-shell :global(.app-sb-toggle-wrap) {
			display: block !important;
		}
		.splash-shell :global(.app-frame) {
			height: 100vh;
		}
		.splash-shell :global(.app-shell) {
			flex-direction: row;
		}
		.splash-shell :global(.app-inset) {
			margin: 8px;
			border-radius: 8px;
		}
	}
</style>
